import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { prop } from 'styled-tools';
import { Col, Row } from 'reactstrap';
import SectionsWrapper from 'components/SectionsWrapper';
import SectionDivider from 'components/SectionDivider';
import ReadyToTrade from 'components/ReadyToTrade';
import TastyButton from 'components/TastyButton';
import SmallPrint from 'components/SmallPrint';
import Section from 'components/Section';
import Layout from 'components/Layout';
import Hero from 'components/Hero';
import SEO from 'components/SEO';
import externalLinks from 'utils/externalLinks';
import activeTraderImg from 'images/image-desktop-active.png';
import futuresHero from 'images/hero/TT1204_Header_Futures01a.jpg';

const H2 = styled.h2`
  color: ${prop('theme.colors.black')};
  font-size: 1.625rem;
  font-weight: 700;
  text-transform: uppercase;

  @media (min-width: ${prop('theme.breakpoints.md')}) {
    font-size: 2.25rem;
  }
`;

const H3 = styled.h3`
  font-weight: 700;
  text-transform: uppercase;
`;

const CalloutWrapper = styled.div`
  display: inline-block;
  margin-top: 1rem;
  margin-bottom: 1rem;
  text-align: center;
  width: fit-content;

  @media (min-width: ${prop('theme.breakpoints.md')}) {
    display: block;
  }
`;

const CalloutCol = styled(Col)`
  text-align: center;
  order: -1;
  margin-bottom: 1rem;

  @media (min-width: ${prop('theme.breakpoints.md')}) {
    order: 0;
    text-align: left;
    margin-bottom: 0;
  }
`;

const CalloutTitle = styled.span`
  color: ${prop('theme.colors.primaryRed')};
  font-size: 1.1rem;
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1;

  @media (min-width: ${prop('theme.breakpoints.sm')}) {
    font-size: 1.2rem;
  }
`;

const CalloutText = styled.span`
  color: ${prop('theme.colors.black')};
  font-size: 3.25rem;
  font-weight: bold;
  letter-spacing: -0.25rem;
  line-height: 0.7;

  @media (min-width: ${prop('theme.breakpoints.sm')}) {
    font-size: 4.5rem;
    line-height: 0.8;
  }

  sup {
    font-size: 0.6em;
  }
`;

const CalloutMuted = styled.span`
  color: ${prop('theme.colors.lightGray')};
  font-size: 1rem;
  font-weight: bold;
  line-height: 1;
`;

const ComingSoon = styled.span`
  color: ${prop(('theme.colors.primaryRed'))};
`

const ImageCol = styled(Col)`
  text-align: center;
  margin-bottom: 1.5rem;

  @media (min-width: ${prop('theme.breakpoints.md')}) {
    text-align: left;
    margin-bottom: 0;
  }

  img {
    max-width: 100%;
  }
`;

function FuturesPage({ showPromoBar }) {
  return (
    <Layout>
      <SEO
        title='Futures Trading Platform'
        description='A futures trading platform for the active trader. tastyworks has one of the lowest commission rates in the futures brokerage industry - $1.25/contract (plus clearing/exchange fees).'
        canonicalHref='https://tastyworks.com/futures'
      />
      <Hero heroImage={futuresHero} showPromoBar={showPromoBar}>
        <h1>Futures Are Now!</h1>
      </Hero>
      <SectionsWrapper noGutters>
        <Section withPadding className='pt-5'>
          <Row>
            <Col>
              <H2>Futures trading for the active trader</H2>
              <p>
                Trading futures at tastyworks is fast, reliable, and inexpensive. What else could you ask for from a futures broker?
              </p>
            </Col>
          </Row>
        </Section>
        <Section withPadding className='pb-3'>
          <SectionDivider />
          <Row>
            <Col>
              <H2>Futures Trading Commission Rates</H2>
              <p>
                Our futures commissions are low and straightforward. You’re here to make money, not spend it.
              </p>
              <Row>
                <CalloutCol xs={4}>
                  <CalloutWrapper>
                    <CalloutTitle>Futures*</CalloutTitle><br />
                    <CalloutText><sup>$</sup>1.25</CalloutText><br />
                    <CalloutMuted>per contract</CalloutMuted>
                  </CalloutWrapper>
                </CalloutCol>
                <CalloutCol xs={4}>
                  <CalloutWrapper>
                    <CalloutTitle>E-MICROS*</CalloutTitle><br />
                    <CalloutText><sup>$</sup>0.85</CalloutText><br />
                    <CalloutMuted>per contract</CalloutMuted>
                  </CalloutWrapper>
                </CalloutCol>
                <CalloutCol xs={4}>
                  <CalloutWrapper>
                    <CalloutTitle>SMALLS*</CalloutTitle><br />
                    <CalloutText><sup>$</sup>0.25</CalloutText><br />
                    <CalloutMuted>per contract</CalloutMuted>
                  </CalloutWrapper>
                </CalloutCol>
              </Row>
              <SmallPrint>
                * Some additional fees will be charged on both opening and closing trades for all products.
              </SmallPrint>
              <p><Link to='/commissions-and-fees'>View Detailed Pricing and Fees</Link></p>
            </Col>
          </Row>
        </Section>
        <Section withPadding className='pb-3'>
          <SectionDivider />
          <H2 className='mb-3'>Our Futures Products</H2>
          <H3>SMFE Futures</H3>
          <p>
            <a href={externalLinks.smallsProductsNoCampaign} target="_blank">
              The Small Exchange
            </a>
            {' '}
            offers products that are small, standard, and simple so more people can participate in futures markets.
          </p>
          <Row className='mb-3'>
            <Col xs={12} md={8}>
              <h4>The Smalls</h4>
              <ul>
                <li>Small Stocks 75 (/SM75)</li>
                <li>Small Technology 60 (/STIX)</li>
                <li>Small Precious Metals (/SPRE)</li>
                <li>Small U.S. Dollar (/SFX)</li>
                <li>Small Global Oil (/SMGO) - <ComingSoon className="coming-soon">COMING SOON!</ComingSoon></li>
                <li>Small 10 Year U.S. Treasury Yield (/S10Y) - <ComingSoon className="coming-soon">COMING SOON!</ComingSoon></li>
              </ul>
            </Col>
          </Row>
          <H3>CME Futures</H3>
          <p>The CME offers some of the most familiar futures products you can trade.</p>
          <Row>
            <Col xs={12} md={4}>
              <h4>Equity Index Futures</h4>
              <ul>
                <li>E-mini S&P 500 (/ES)</li>
                <li>Micro E-mini S&P 500 (/MES)</li>
                <li>E-mini Nasdaq 100 (/NQ)</li>
                <li>Micro E-mini NASDAQ 100 (/MNQ)</li>
                <li>E-mini Dow 30 (/YM)</li>
                <li>Micro E-mini DOW (/MYM)</li>
                <li>E-mini Russell 2000 (/RTY)</li>
                <li>Micro E-mini Russell 2000 (/M2K)</li>
              </ul>
            </Col>
            <Col xs={12} md={4}>
              <h4>Interest Rate Futures</h4>
              <ul>
                <li>2 Year T-Note (/ZT)</li>
                <li>5 Year T-Note (/ZF)</li>
                <li>10 Year T-Note (/ZN)</li>
                <li>30 Year T-Bond (/ZB)</li>
                <li>90 Day Eurodollar (/GE)</li>
                <li>Ultra U.S. Treasury Bonds (/UB)</li>
              </ul>
            </Col>
            <Col xs={12} md={4}>
              <h4>Foreign Currency Futures</h4>
              <ul>
                <li>Euro FX (/6E)</li>
                <li>Micro Euro (/M6E)</li>
                <li>Japanese Yen (/6J)</li>
                <li>Micro Japanese Yen (/MJY)</li>
                <li>British Pound (/6B)</li>
                <li>Micro British Pound (/M6B)</li>
                <li>Canadian Dollar (/6C)</li>
                <li>Micro Canadian Dollar (/MCD)</li>
                <li>Australian Dollar (/6A)</li>
                <li>Micro Australian Dollar (/M6A)</li>
              </ul>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={4}>
              <h4>Energy Futures</h4>
              <ul>
                <li>Crude Oil (/CL)</li>
                <li>E-mini Crude Oil (/QM)</li>
                <li>Henry Hub Natural Gas (/NG)</li>
                <li>E-mini Natural Gas (/QG)</li>
              </ul>
            </Col>
            <Col xs={12} md={4}>
              <h4>Metals Futures</h4>
              <ul>
                <li>Gold (/GC)</li>
                <li>Micro Gold (/MGC)</li>
                <li>Copper (/HG)</li>
                <li>Silver (/SI)</li>
                <li>Micro Silver (/SIL)</li>
              </ul>
            </Col>
            <Col xs={12} md={4}>
              <h4>Agricultural Futures</h4>
              <ul>
                <li>Corn Futures (/ZC)</li>
                <li>Mini Corn (/XC)</li>
                <li>Soybean (/ZS)</li>
                <li>Mini Soybean (/XK)</li>
                <li>Chicago SRW Wheat (/ZW)</li>
                <li>Mini Chicago SRW Wheat (/XW)</li>
              </ul>
            </Col>
          </Row>
        </Section>
        <Section className='pb-5' withPadding>
          <SectionDivider />
          <Row>
            <ImageCol xs={12} md={4}>
              <img src={activeTraderImg} alt='Active trader interface on desktop computer' />
            </ImageCol>
            <Col xs={12} md={8}>
              <H2>Our Trading Platform</H2>
              <p>Speed is everything when you’re trading futures. The tastyworks platform
                includes <a href={externalLinks.activeTraderInterface} target='_blank'>our unique active trader interface</a> for fast order entry and exit,
                and our high-frequency trading middleware gives you fast fills at the prices you like.
              </p>
            </Col>
          </Row>
        </Section>
        <ReadyToTrade />
      </SectionsWrapper>
    </Layout>
  )
}

export default FuturesPage;
